// getLocalizationDefaultColumns
import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import RenderTextfieldHeader from '../../renderTextFieldHeader';
import FeedIcon from '@mui/icons-material/Feed';
import EditIcon from '@mui/icons-material/Edit';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { colorTypes, columnTypes, dateConfig, regionalFields, renderDateCell, renderTextCell } from './columnTypes';
import { renderOcplcSelectHeader } from 'pages/ocplcDashboard/ocplcHelpers';


const createTextColumn = (field, label, filters, handleChange, getColor = null) => ({
    headerName: label,
    field,
    width: 155,
    sortable: false,
    headerClassName: 'dashboard-data-grid-headers',
    renderCell: (params) => renderTextCell(params.row[field], getColor ? getColor(params.row.status) : undefined),
    renderHeader: () => (
        <RenderTextfieldHeader colData={columnTypes[field]} label={label} filters={filters} handleChange={handleChange} />
    ),
});

const createDateColumn = (field, label, filters, handleChange) => {
  const columnType = field === 'regStartDate' ? 'startDate' : field === 'regEndDate' ? 'endDate' : field;
    return{
        headerName: label,
        field,
        width: 155,
        sortable: false,
        headerClassName: 'dashboard-data-grid-headers',
        renderCell: (params) => renderDateCell(params.row[field]),
        renderHeader: () => (
            <RenderTextfieldHeader colData={columnTypes[columnType]} label={label} filters={filters} handleChange={handleChange} />
        ),
    }
};

const createSelectColumn = (field, label, filters, handleChange, dropdownData) => ({
    headerName: label,
    field,
    width: 155,
    sortable: false,
    headerClassName: 'dashboard-data-grid-headers',
    renderCell: (params) => renderTextCell(params.row[field]),
    renderHeader: () => renderOcplcSelectHeader(columnTypes[field], filters, label, handleChange, dropdownData),
});

const createCheckboxColumn = (field) => ({
    headerName: field.slice(0, 2).toUpperCase(),
    field,
    width: 40,
    sortable: false,
    headerClassName: 'dashboard-data-grid-checkbox-headers',
    renderCell: (params) => <Checkbox disabled checked={params.row[field] === true} />,
});


export const getLocalizationAvailabilityColumns = (
    filters,
    localizationAvailabilityData = [],
    handleChange,
    countryList,
    businessModels,
    regionDropdown,
    handleViewModel,
    region,
    disableAction,
    handleFeedDialog
) => {

    const isFieldNotEmpty = (field) =>
        localizationAvailabilityData.length > 0 && localizationAvailabilityData.every((row) => row[field]);

    const { startDateLabel, endDateLabel, startDateValue, endDateValue } = dateConfig[region] || dateConfig.default;

    const baseColumns = [
        createTextColumn('productFamily', 'Product Family', filters, handleChange,(status) => colorTypes[status] || 'grey'),
        createTextColumn('feature', 'Feature', filters, handleChange),
        createTextColumn('featureValue', 'Feature Value', filters, handleChange),
        createTextColumn('featureDesc', 'Feature Description', filters, handleChange),
        createSelectColumn('region', 'Region', filters, handleChange, regionDropdown),
        createTextColumn('status', 'Status', filters, handleChange),
        createSelectColumn('country', 'Country ID', filters, handleChange, countryList),
        createTextColumn('countryDesc', 'Country Description', filters, handleChange),
        createTextColumn('comments', 'Comments', filters, handleChange),
        createDateColumn(startDateValue, startDateLabel, filters, handleChange),
        region !== 'WW' && createDateColumn('regStartDate', 'Start Date', filters, handleChange),
        region !== 'WW' && createDateColumn('regEndDate', 'End Date', filters, handleChange),
        createDateColumn(endDateValue, endDateLabel, filters, handleChange),
    ].filter(Boolean);

    const regionalDateColumns = regionalFields.flatMap(({ startField, startName, endField, endName }) => [
        isFieldNotEmpty(startName) && createDateColumn(startField, localizationAvailabilityData?.[0]?.[startName], filters, handleChange),
        isFieldNotEmpty(endName) && createDateColumn(endField, localizationAvailabilityData?.[0]?.[endName], filters, handleChange),
    ]).filter(Boolean);

    const authorColumn = createTextColumn('whoChanged', 'Author', filters, handleChange);

    const regionCheckBoxColumns = region === 'WW'
        ? ['apRegion', 'euRegion', 'laRegion', 'naRegion'].map(createCheckboxColumn)
        : [];

   const actionColumn = {
    headerName: '',
    field: 'actions',
    width: 180,
    sortable: false,
    renderCell: (params) => {
        const actions = [
            {
                title: 'Audit Trail - Localization Availability',
                icon: <FeedIcon />,
                onClick: () => handleFeedDialog(params.row, 'avilableLocAudit'),
            },
            {
                title: disableAction ? 'View' : 'Edit',
                icon: disableAction ? <VisibilityIcon /> : <EditIcon />,
                onClick: () => handleViewModel(params.row),
            }
        ];

        return (
            <Box onClick={(e) => e.stopPropagation()}>
                {actions.map((action, idx) => (
                    <Tooltip title={action.title} key={idx}>
                        <Button onClick={action.onClick} startIcon={action.icon} size="medium" />
                    </Tooltip>
                ))}
            </Box>
        );
    },
};


    return [...baseColumns, ...regionalDateColumns, authorColumn, ...regionCheckBoxColumns, actionColumn];
};
